const AUTH_STORAGE_KEYS = {
  accessToken: 'authToken',
  refreshToken: 'refreshToken',
  userData: 'userData',
  tenantSlug: 'authTenant',
  tenantNombre: 'authTenantNombre',
  tenantContext: 'authTenantContext',
  crossTenantWarning: 'authCrossTenantWarning',
};

function normalizeBaseUrl(value, fallback = '') {
  const normalized = (value || fallback || '').trim().replace(/\/+$/, '');
  return normalized || fallback;
}

function getDefaultApiRoot() {
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000/api';
  }

  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  const host = window.location.hostname || '127.0.0.1';
  return `${protocol}//${host}:8000/api`;
}

function buildTenantContext(explicitApiRoot) {
  if (typeof window === 'undefined') {
    const apiRoot = normalizeBaseUrl(explicitApiRoot, getDefaultApiRoot());
    return {
      frontendHost: 'server',
      apiRoot,
      apiHost: '127.0.0.1',
    };
  }

  const apiRoot = normalizeBaseUrl(explicitApiRoot, getDefaultApiRoot());
  let apiHost = window.location.hostname || '127.0.0.1';

  try {
    apiHost = new URL(apiRoot).hostname || apiHost;
  } catch {
    apiHost = window.location.hostname || apiHost;
  }

  return {
    frontendHost: window.location.hostname || '127.0.0.1',
    apiRoot,
    apiHost,
  };
}

function getStoredTenantContext() {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEYS.tenantContext);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getStoredTenantSlug() {
  return localStorage.getItem(AUTH_STORAGE_KEYS.tenantSlug);
}

function getStoredTenantNombre() {
  return localStorage.getItem(AUTH_STORAGE_KEYS.tenantNombre);
}

function saveAuthSession({ access, refresh, user, tenant, tenantNombre, tenantContext }) {
  if (access) {
    localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, access);
  }
  if (refresh) {
    localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, refresh);
  }
  if (user) {
    localStorage.setItem(AUTH_STORAGE_KEYS.userData, JSON.stringify(user));
  }
  if (tenant) {
    localStorage.setItem(AUTH_STORAGE_KEYS.tenantSlug, tenant);
  }
  if (tenantNombre) {
    localStorage.setItem(AUTH_STORAGE_KEYS.tenantNombre, tenantNombre);
  }

  const context = tenantContext || buildTenantContext(process.env.REACT_APP_API_ROOT);
  localStorage.setItem(AUTH_STORAGE_KEYS.tenantContext, JSON.stringify(context));
}

function clearAuthSession() {
  Object.values(AUTH_STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
}

function getCrossTenantSessionDetails(explicitApiRoot) {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  const user = localStorage.getItem(AUTH_STORAGE_KEYS.userData);
  if (!(token && user)) {
    return null;
  }

  const storedContext = getStoredTenantContext();
  const currentContext = buildTenantContext(explicitApiRoot);

  if (!storedContext) {
    return {
      reason: 'missing_stored_context',
      storedContext: null,
      currentContext,
    };
  }

  const sameHost = storedContext.frontendHost === currentContext.frontendHost;
  const sameApiHost = storedContext.apiHost === currentContext.apiHost;

  if (sameHost && sameApiHost) {
    return null;
  }

  return {
    reason: 'tenant_context_mismatch',
    storedContext,
    currentContext,
  };
}

function saveCrossTenantWarning(details) {
  if (!details) {
    return;
  }

  const warning = {
    type: 'cross_tenant_session_detected',
    at: new Date().toISOString(),
    ...details,
  };

  localStorage.setItem(AUTH_STORAGE_KEYS.crossTenantWarning, JSON.stringify(warning));
}

function consumeCrossTenantWarning() {
  const raw = localStorage.getItem(AUTH_STORAGE_KEYS.crossTenantWarning);
  if (!raw) {
    return null;
  }

  localStorage.removeItem(AUTH_STORAGE_KEYS.crossTenantWarning);

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isSessionCompatibleWithCurrentTenant(explicitApiRoot) {
  return !getCrossTenantSessionDetails(explicitApiRoot);
}

export {
  AUTH_STORAGE_KEYS,
  normalizeBaseUrl,
  getDefaultApiRoot,
  buildTenantContext,
  getStoredTenantContext,
  getStoredTenantSlug,
  getStoredTenantNombre,
  saveAuthSession,
  clearAuthSession,
  getCrossTenantSessionDetails,
  saveCrossTenantWarning,
  consumeCrossTenantWarning,
  isSessionCompatibleWithCurrentTenant,
};
